import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  Fragment,
} from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DocCenterService } from "../services/docCenterService.js";
import { CmsService } from "../services/cmsService";
import downloadFile from "../utils/fileUtils";
import Calendar from "../components/Calendar";
import NoItem from "../components/NoItem";
import LoadingScreen from "../components/LoadingScreen";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import Icon from "../components/ui/Icon";

function LandingPage() {
  const [loading, setLoading] = useState(true);
  const { t, i18n } = useTranslation();
  const [links, setLinks] = useState([]);
  const [communications, setCommunications] = useState([]);
  const [isPin, setIsPin] = useState([]);
  const [isPromo, setIsPromo] = useState([]);
  const dcService = useMemo(() => new DocCenterService(), []);
  const cmsService = useMemo(() => new CmsService(), []);

  const savedLanguage = useMemo(() => i18n.language, [i18n.language]);

  const navigate = useNavigate();

  const EditDocument = useCallback(
    (event) => navigate("/ViewDocument", { state: { event } }),
    [navigate]
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const pinResponse = await dcService.retrieveDocumentCenterList(
          "PIN",
          0,
          40
        );
        setIsPin(pinResponse.data.documentCenterList);

        const promoResponse = await dcService.retrieveDocumentCenterList(
          "PROMO",
          0,
          40
        );
        setIsPromo(promoResponse.data.documentCenterList);

        const communicationsResponse =
          await cmsService.retrieveLandingCommunications(0, 40);

        var pinCommunication =
          communicationsResponse.data.tcmsBrokerCommunicationEntityList.filter(
            (a) =>
              new Date(a.endDate) > new Date() &&
              new Date(a.startDate) < new Date() &&
              a.isPin === "Y"
          );
        pinCommunication.push(
          ...communicationsResponse.data.tcmsBrokerCommunicationEntityList.filter(
            (a) =>
              new Date(a.endDate) > new Date() &&
              new Date(a.startDate) < new Date() &&
              a.isPin !== "Y"
          )
        );
        setCommunications(pinCommunication);

        const linksResponse = await cmsService.retrieveLinks(0, 40);
        setLinks(linksResponse.data.tcmsQuickLinkEntityList);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false); // Set loading to false after data retrieval
      }
    };

    fetchData();
  }, [cmsService, dcService]);

  const LandingBox = useCallback(
    ({ title, titleProps, content, contentProps }) => (
      <Card raised sx={{ m: 1 }}>
        <CardHeader
          title={t(title)}
          sx={{ bgcolor: "primary.main", color: "white", p: 1 }}
          titleTypographyProps={{
            variant: "body1",
            textAlign: "center",
            fontWeight: "bold",
          }}
          {...titleProps}
        />
        <CardContent>
          <Grid
            container
            sx={{ justifyContent: "center", maxHeight: 215, overflow: "auto" }}
            {...contentProps}>
            {content}
          </Grid>
        </CardContent>
      </Card>
    ),
    [t]
  );

  const FileDownloadButton = useCallback(
    ({ fileId, fileName }) => (
      <Tooltip title={fileName} arrow>
        <Button
          startIcon={<Icon src="file" />}
          onClick={() => downloadFile(fileId, fileName)}
          size="small">
          {fileName}
        </Button>
      </Tooltip>
    ),
    []
  );

  return loading ? (
    <LoadingScreen />
  ) : (
    <Grid
      container
      item
      sx={{ m: "auto", justifyContent: "center" }}
      xs={12}
      lg={11}
      xl={9}>
      <Grid item xs={12} lg={8}>
        <LandingBox
          title="landingPage.brokersCommunications"
          content={
            !communications || communications.length === 0 ? (
              <NoItem />
            ) : (
              <List
                sx={{
                  width: "100%",
                  maxHeight: 400,
                  position: "relative",
                  p: 0,
                }}>
                {communications.map((communication, index) => (
                  <ListItemButton key={index}>
                    <ListItemText
                      primaryTypographyProps={{ variant: "body2" }}
                      primary={`${t("landingPage.subject")}: ${
                        savedLanguage === "en"
                          ? communication.communicationNameEng
                          : savedLanguage === "hk"
                          ? communication.communicationNameHk
                          : communication.communicationNameCn
                      }`}
                      secondaryTypographyProps={{ variant: "caption" }}
                      secondary={`${t("common.publishDate")}: ${
                        communication.startDate
                      }`}
                    />
                    {communication.isPin === "Y" && (
                      <ListItemAvatar>
                        <Icon src="bookmark" color="primary" />
                      </ListItemAvatar>
                    )}
                  </ListItemButton>
                ))}
              </List>
            )
          }
        />

        <LandingBox
          title="landingPage.latestPromo"
          content={
            !isPromo || isPromo.length === 0 ? (
              <NoItem />
            ) : (
              <List
                sx={{
                  width: "100%",
                  maxHeight: 400,
                  position: "relative",
                  p: 0,
                }}>
                {isPromo.map((promo, index) => (
                  <ListItemButton
                    key={index}
                    divider
                    onClick={() => EditDocument(promo)}>
                    <Stack>
                      <ListItemText
                        primaryTypographyProps={{ variant: "body2" }}
                        primary={
                          new Date() >= new Date(promo.effectiveDateTo) ? (
                            <>{t("common.expired")}</>
                          ) : new Date() <=
                            new Date(promo.effectiveDateFrom) ? (
                            <>{t("common.unposted")}</>
                          ) : null
                        }
                        secondary={
                          <Fragment>
                            <Typography variant="caption">
                              {savedLanguage === "en"
                                ? promo.titleEng
                                : savedLanguage === "cn"
                                ? promo.titleCn
                                : promo.titleHk}
                            </Typography>
                          </Fragment>
                        }
                      />

                      <Grid container rowSpacing={1}>
                        {promo.file1Name && (
                          <FileDownloadButton
                            fileId={promo.file1Id}
                            fileName={promo.file1Name}
                          />
                        )}
                        {promo.file2Name && (
                          <FileDownloadButton
                            fileId={promo.file2Id}
                            fileName={promo.file2Name}
                          />
                        )}
                        {promo.file3Name && (
                          <FileDownloadButton
                            fileId={promo.file3Id}
                            fileName={promo.file3Name}
                          />
                        )}
                      </Grid>
                    </Stack>
                  </ListItemButton>
                ))}
              </List>
            )
          }
        />

        <LandingBox
          title="landingPage.eventCalendar"
          content={<Calendar />}
          contentProps={{ sx: { maxHeight: "auto", justifyContent: "center" } }}
        />
      </Grid>

      <Grid item xs={12} lg={3}>
        <LandingBox
          title="landingPage.documentCentre"
          content={
            !isPin || isPin.length === 0 ? (
              <NoItem />
            ) : (
              isPin.map((pin, index) => (
                <Button
                  key={index}
                  fullWidth
                  startIcon={<Icon src="file" />}
                  onClick={() => EditDocument(pin)}
                  sx={{
                    justifyContent: "flex-start",
                    textAlign: "left",
                  }}>
                  {new Date() >= new Date(pin.effectiveDateTo) ? (
                    <>{t("common.expired")}</>
                  ) : new Date() <= new Date(pin.effectiveDateFrom) ? (
                    <>{t("common.unposted")}</>
                  ) : null}
                  {savedLanguage === "en"
                    ? pin.titleEng
                    : savedLanguage === "cn"
                    ? pin.titleCn
                    : pin.titleHk}
                </Button>
              ))
            )
          }
        />
        <LandingBox
          title="landingPage.quickLinks"
          content={
            !links || links.length === 0 ? (
              <NoItem />
            ) : (
              links.map((link, index) => (
                <Button
                  key={index}
                  fullWidth
                  startIcon={<Icon src="arrowRight" />}
                  color="inherit"
                  sx={{
                    justifyContent: "flex-start",
                    textAlign: "left",
                  }}>
                  <a
                    href={link.url}
                    className="no-underline text-ft-light"
                    target="_blank"
                    rel="noreferrer">
                    {savedLanguage === "en"
                      ? link.linkNameEng
                      : savedLanguage === "cn"
                      ? link.linkNameCn
                      : link.linkNameHk}
                  </a>
                </Button>
              ))
            )
          }
        />
      </Grid>
    </Grid>
  );
}

export default LandingPage;
